// Canonical site identity used by SEO metadata, JSON-LD, and sitemap generation.
// FR-C4 chose `www.eagleharboradvisors.com` as the canonical host (apex 301s to www
// at the App Engine layer — see app.yaml).
export const SITE_ORIGIN = "https://www.eagleharboradvisors.com";
export const SITE_NAME = "Eagle Harbor Advisors";
export const SITE_TAGLINE =
  "Wealth Management & Financial Planning in Boca Raton, Florida";
// Per-page copy is grounded in the CMS at eha-strapi-022c51a3f710.herokuapp.com
// and the firm's actual positioning (capital preservation, effective compounding,
// downside protection, proprietary models, specialized practice areas).
// Forbes recognition references individual managing partners only — Glen Turner
// (Best-In-State Wealth Advisors 2026) and Andy Wilkins (Best-In-State Wealth
// Advisors 2024) — and is asserted as "led by" or "team includes" language,
// never as a firm-level claim.
export const SITE_DEFAULT_DESCRIPTION =
  "Eagle Harbor Advisors — personalized wealth management and financial planning in Boca Raton, FL. Our team includes advisors named to Forbes' Best-In-State Wealth Advisors list — strategies tailored to each client's goals, time horizon, and risk tolerance.";
export const SITE_DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/og-default.jpg`;
export const SITE_TWITTER_HANDLE = "";

export const ORG_PHONE = "+1-561-576-8280";
export const ORG_ADDRESS = {
  street: "2650 N Military Trail Suite 420",
  locality: "Boca Raton",
  region: "FL",
  postalCode: "33431",
  country: "US",
};
// Geo for 2650 N Military Trail, Boca Raton, FL 33431 (approximate; from public map data).
export const ORG_GEO = { lat: 26.3956, lng: -80.1305 };
// Canonical social profiles for the firm. Sourced from
// src/Components/SocialMediaBlock/SocialMediaBlock.tsx (the live footer icons),
// which is the in-repo source of truth. Update both places if either changes.
export const ORG_SAME_AS: string[] = [
  "https://www.facebook.com/ehadvisor/",
  "https://www.instagram.com/safe_with_eagleharbor/",
  "https://twitter.com/GSmartMoney",
  "https://www.linkedin.com/company/77159197",
  "https://www.youtube.com/channel/UC7H7o70-Kjy0UhJD6kmwAXQ",
];

// Per-advisor identity profile — drives the Person JSON-LD schemas on /about.
//
// `award` + `forbesProfile` are *individual* Forbes Best-In-State Wealth
// Advisors recognitions (not firm-level), surfaced in both the About page
// title and the Person.award JSON-LD field.
//
// `linkedin` is the advisor's public LinkedIn URL. Combined with
// `forbesProfile`, these populate the Person.sameAs JSON-LD array — schema.org
// uses sameAs to disambiguate the entity across the web. LinkedIn URLs are
// kept URL-encoded (%C2%AE = ®, %E2%84%A2 = ™) since that's the canonical wire
// format and the safest input for downstream validators.
//
// All fields except `name` are optional — advisors without Forbes recognition
// or without a public LinkedIn simply don't get those fields in their schema.
export type AdvisorProfile = {
  name: string;
  award?: string;
  forbesProfile?: string;
  linkedin?: string;
  year?: number;
};

export const ADVISOR_PROFILES: AdvisorProfile[] = [
  {
    name: "Glen Turner",
    award: "Forbes Best-In-State Wealth Advisors 2026 (Florida, #195)",
    forbesProfile: "https://www.forbes.com/profile/glen-turner",
    linkedin:
      "https://www.linkedin.com/in/glen-turner-crpc%C2%AE-cpfa%C2%AE-c-k-p%C2%AE-39937216/",
    year: 2026,
  },
  {
    name: "Andy Wilkins",
    award: "Forbes Best-In-State Wealth Advisors 2024 (Florida, #142)",
    forbesProfile: "https://www.forbes.com/profile/andy-wilkins",
    linkedin:
      "https://www.linkedin.com/in/andrew-wilkins-cfp%C2%AE-se-awma%E2%84%A2%EF%B8%8F-cepa%C2%AE-ba237a30/",
    year: 2024,
  },
  {
    name: "Girish Navare",
    linkedin: "https://www.linkedin.com/in/smartmoneyadviser/",
  },
];

export type RouteSeo = {
  path: string;
  title: string;
  description: string;
  ogType?: "website" | "article" | "profile";
  changefreq?: "always" | "hourly" | "daily" | "weekly" | "monthly" | "yearly" | "never";
  priority?: number;
  breadcrumb: { name: string; path: string }[];
};

// Single source of truth for route-level SEO + sitemap generation.
export const ROUTE_SEO: RouteSeo[] = [
  {
    path: "/",
    title:
      "Eagle Harbor Advisors — Wealth Management & Financial Planning | Boca Raton, FL",
    description:
      "Personalized wealth management and financial planning in Boca Raton, FL. Our team includes advisors named to Forbes' Best-In-State Wealth Advisors list — strategies tailored to each client's goals, time horizon, and risk tolerance.",
    ogType: "website",
    changefreq: "weekly",
    priority: 1.0,
    breadcrumb: [{ name: "Home", path: "/" }],
  },
  {
    path: "/about",
    title:
      "Our Team — Forbes Best-In-State Recognized Advisors | Eagle Harbor Advisors",
    description:
      "Meet the Eagle Harbor Advisors team. Glen Turner (Forbes Best-In-State Wealth Advisors 2026) and Andy Wilkins (Forbes Best-In-State Wealth Advisors 2024) lead our Boca Raton wealth management practice — guided by the principles of expertise, accountability, and caring.",
    ogType: "website",
    changefreq: "monthly",
    priority: 0.8,
    breadcrumb: [
      { name: "Home", path: "/" },
      { name: "About", path: "/about" },
    ],
  },
  {
    path: "/services",
    title:
      "Wealth Management, Investment & Financial Planning Services | Eagle Harbor Advisors",
    description:
      "Comprehensive financial planning, wealth management, retirement, and estate planning for individuals, families, and business owners. Built on capital preservation and effective compounding from our Boca Raton, FL practice.",
    ogType: "website",
    changefreq: "monthly",
    priority: 0.9,
    breadcrumb: [
      { name: "Home", path: "/" },
      { name: "Services", path: "/services" },
    ],
  },
  {
    path: "/pilots",
    title:
      "Wealth Management for Pilots & Aviation Professionals | Eagle Harbor Advisors",
    description:
      "Specialized financial planning for commercial pilots, corporate aviators, and aviation professionals. Schedule disruption, variable compensation, retirement timing, and stock-option strategies handled by Eagle Harbor's Boca Raton wealth team.",
    ogType: "website",
    changefreq: "monthly",
    priority: 0.9,
    breadcrumb: [
      { name: "Home", path: "/" },
      { name: "Pilots", path: "/pilots" },
    ],
  },
  {
    path: "/business-solutions",
    title:
      "Wealth Management for Business Owners — Exit & Succession Planning | Eagle Harbor Advisors",
    description:
      "Financial planning for business owners. Exit and succession planning, 401(k)/profit-sharing plan design, and executive compensation strategy. Personalized wealth management from Eagle Harbor's Boca Raton, FL practice.",
    ogType: "website",
    changefreq: "monthly",
    priority: 0.9,
    breadcrumb: [
      { name: "Home", path: "/" },
      { name: "Business Solutions", path: "/business-solutions" },
    ],
  },
  {
    path: "/resources",
    title:
      "Insights, Articles & Financial Resources | Eagle Harbor Advisors",
    description:
      "Expert articles, market commentary, and financial-planning tools curated by the Eagle Harbor Advisors team. Planning insights for individuals, families, and business owners. Updated regularly.",
    ogType: "website",
    changefreq: "weekly",
    priority: 0.7,
    breadcrumb: [
      { name: "Home", path: "/" },
      { name: "Resources", path: "/resources" },
    ],
  },
  {
    path: "/portfolio",
    title:
      "Investment Approach & Portfolio Construction | Eagle Harbor Advisors",
    description:
      "How Eagle Harbor builds resilient portfolios — capital preservation, effective compounding, and proprietary models for downside protection. Strategic and tactical allocation aligned to each client's goals, time horizon, and risk tolerance.",
    ogType: "website",
    changefreq: "monthly",
    priority: 0.7,
    breadcrumb: [
      { name: "Home", path: "/" },
      { name: "Portfolio", path: "/portfolio" },
    ],
  },
  {
    path: "/events",
    title: "Upcoming Events & Webinars | Eagle Harbor Advisors",
    description:
      "Register for Eagle Harbor's upcoming financial-planning events and on-demand webinars — covering retirement, pilot planning, business owner exit strategy, executive compensation, and more.",
    ogType: "website",
    changefreq: "weekly",
    priority: 0.7,
    breadcrumb: [
      { name: "Home", path: "/" },
      { name: "Events", path: "/events" },
    ],
  },
  {
    path: "/emc2",
    title: "EMC² — Eagle Harbor's Investment Philosophy | Eagle Harbor Advisors",
    description:
      "EMC² is Eagle Harbor's proprietary investment philosophy. Built on capital preservation, effective compounding, downside protection, and strategic & tactical allocations to minimize risk and maximize returns for each client's unique needs.",
    ogType: "website",
    changefreq: "monthly",
    priority: 0.6,
    breadcrumb: [
      { name: "Home", path: "/" },
      { name: "EMC²", path: "/emc2" },
    ],
  },
  {
    path: "/risk-profiler",
    title: "Risk Tolerance Profiler | Eagle Harbor Advisors",
    description:
      "Use Eagle Harbor's risk-tolerance assessment to clarify how your portfolio should be positioned. A short questionnaire designed by our Boca Raton wealth team.",
    ogType: "website",
    changefreq: "yearly",
    priority: 0.5,
    breadcrumb: [
      { name: "Home", path: "/" },
      { name: "Risk Profiler", path: "/risk-profiler" },
    ],
  },
  {
    path: "/calendly",
    title: "Schedule a Consultation | Eagle Harbor Advisors",
    description:
      "Book a consultation with an Eagle Harbor advisor to discuss your wealth management goals. Our Boca Raton, FL team works with individuals, families, and business owners across industries — locally and nationwide.",
    ogType: "website",
    changefreq: "yearly",
    priority: 0.6,
    breadcrumb: [
      { name: "Home", path: "/" },
      { name: "Schedule", path: "/calendly" },
    ],
  },
];

export const findRouteSeo = (path: string): RouteSeo | undefined => {
  // Exact match first, then prefix match (handles nested-path routes like /events/foo).
  const exact = ROUTE_SEO.find((r) => r.path === path);
  if (exact) return exact;
  return ROUTE_SEO.find((r) => r.path !== "/" && path.startsWith(r.path));
};
